/**
 * 产物形制图示的单一真值源(2026-07-23 卡片重构 R2)。
 *
 * 卡面左下角画一个**几何图示**告诉用户「出来的是 1 张方图 / 1 条长图 / 3 个比例 / 1 条宽图」。
 * 这是真样图裁成 4:3 之后永远表达不了的一维 —— `2160×9727` 的长图裁完只剩顶部一条。
 *
 * ★工程约定:图示用**比例**画(`aspect-ratio` + flex),不写死 px;圆角一律相对 `--radius`
 * (2026-07-22 拍板的形状锁)。颜色只用语义 token,三皮肤自动跟随 —— 编号徽标曾写死
 * `hsl(0 0% 100%/.82)` 白底,cyber 下变成浅字浅底看不见,同类坑不再犯。
 */
import type { SceneShape } from "./types";

export interface ShapeMeta {
  /** 卡面形制说明(≤6 字,跟在图示右边)。 */
  label: string;
  /**
   * 图示的画法:
   *  - `frames` 一到多个矩形,`ratio` 是每个矩形的宽高比(w/h)
   *  - `wave`   声音波形条
   */
  kind: "frames" | "wave";
  /** frames 各格宽高比;长图 0.34 = 细高,宽图 3.2 = 扁宽。 */
  ratios?: number[];
  /** 视频/出镜片额外画播放三角 / 人像圆点。 */
  badge?: "play" | "face";
  /**
   * 框内横向分段线条数 —— 只有长图用。
   * ★为什么需要:长图比例 0.4 在 16px 高下只有 6.4px 宽,单看一个细框容易读成「一条线」。
   * 加几条分段线,它才读得出「一页从上往下分了好几段」这个长图的本质(G-A 门实测)。
   */
  bands?: number;
}

export const SHAPE_META: Record<SceneShape, ShapeMeta> = {
  square: { label: "1 张方图", kind: "frames", ratios: [1] },
  carousel: { label: "N 张组图", kind: "frames", ratios: [1, 1, 1] },
  ratios: { label: "3 个比例", kind: "frames", ratios: [0.56, 1, 1.78] },
  long: { label: "1 条长图", kind: "frames", ratios: [0.4], bands: 3 },
  wide: { label: "1 条宽图", kind: "frames", ratios: [2.6] },
  video: { label: "1 条视频", kind: "frames", ratios: [0.56], badge: "play" },
  avatar: { label: "出镜视频", kind: "frames", ratios: [0.56], badge: "face" },
  audio: { label: "1 段音频", kind: "wave" },
  // 文章:A4 纸比例(0.71)+ 4 条文字分段线 —— 与长图(0.4 细高)在 16px 高下肉眼可分。
  article: { label: "1 篇文章", kind: "frames", ratios: [0.71], bands: 4 },
};

/** 波形条的高度百分比(定值,**不是随机** —— 随机会让每次渲染抖动,也测不了)。 */
export const WAVE_BARS = [38, 72, 100, 62, 86, 44, 70, 30] as const;
